// The model library: a panel the toolbar opens, listing the page config's
// entries. The `file` entry opens a file chooser, and dropping a file on the
// right-hand pane picks it too. Opening itself is the library's job
// (adapter.open); this keeps track of what is open and never opens the same
// choice twice at once.

import { el, icon, ICONS } from './dom.js';

function acceptsName( accept, name ) {

	return accept.split( ',' ).some( ( ext ) => name.toLowerCase().endsWith( ext.trim().toLowerCase() ) );

}

export function createModels( { els, store, entries, accept, adapter, status, out, beforeSwitch } ) {

	let userFile = null;
	let loaded = null; // { key, result }
	let loading = null; // { key, promise }
	let selected = null;
	let ready = false;

	/** The picked entry: { id, label, url, file, key }. */
	function current() {

		const choice = entries.find( ( item ) => item.id === selected ) ?? entries[ 0 ];
		if ( choice.id === 'file' && userFile ) {

			return { ...choice, file: userFile, key: `file:${ userFile.name }:${ userFile.size }`, label: userFile.name };

		}

		return { ...choice, file: null, key: choice.id };

	}

	function fallbackId() {

		return ( entries.find( ( item ) => item.default ) ?? entries.find( ( item ) => item.id !== 'file' ) ).id;

	}

	function render() {

		els.modelsList.textContent = '';
		for ( const item of entries ) {

			if ( item.id === 'file' && ! userFile ) continue;
			const active = item.id === selected;
			els.modelsList.append( el( 'button', {
				class: active ? 'pg-tree-item is-active' : 'pg-tree-item',
				type: 'button',
				role: 'option',
				'data-id': item.id,
				'aria-selected': String( active ),
				title: item.url ?? '',
			}, [
				icon( ICONS.model ),
				el( 'span', { text: item.id === 'file' ? userFile.name : item.label } ),
			] ) );

		}

		const file = entries.find( ( item ) => item.id === 'file' );
		if ( file ) els.modelsList.append( el( 'button', { class: 'pg-tree-item pg-tree-action', type: 'button', 'data-action': 'file' }, [
			icon( ICONS.file ),
			el( 'span', { text: file.label } ),
		] ) );
		els.modelLabel.textContent = current().label;

	}

	function setOpen( open ) {

		els.modelsPanel.hidden = ! open;
		els.modelsButton.setAttribute( 'aria-expanded', String( open ) );
		els.modelsButton.classList.toggle( 'is-active', open );
		if ( ! open ) return;
		render();
		( els.modelsList.querySelector( '.is-active' ) ?? els.modelsList.querySelector( 'button' ) )?.focus();

	}

	function pick( id ) {

		if ( id === selected ) return;
		selected = id;
		render();
		switchModel();

	}

	/** Open the picked model unless it already is. Resolves with what adapter.open returned. */
	function ensure() {

		const choice = current();
		if ( loaded?.key === choice.key && ( adapter.isOpen?.( loaded.result ) ?? true ) ) return Promise.resolve( loaded.result );
		if ( loading?.key === choice.key ) return loading.promise;

		loaded = null;
		status( `Opening ${ choice.label }…` );
		const promise = Promise.resolve().then( () => adapter.open( choice ) ).then( ( result ) => {

			if ( loading?.promise === promise ) loading = null;
			loaded = { key: choice.key, result };
			status( `${ choice.label } ready`, { settle: 1400 } );
			return result;

		}, ( error ) => {

			if ( loading?.promise === promise ) loading = null;
			status( `Could not open ${ choice.label }: ${ error.message }`, { error: true } );
			throw error;

		} );
		loading = { key: choice.key, promise };
		return promise;

	}

	function describe( result ) {

		const detail = adapter.describe?.( result );
		return detail ? ` (${ detail })` : '';

	}

	function open() {

		out.debug( `Opening ${ current().label }…` );
		return ensure().then(
			( result ) => out.debug( `${ current().label } is open${ describe( result ) }` ),
			( error ) => out.error( error.message ),
		);

	}

	function switchModel() {

		store.set( 'model', selected === 'file' ? null : selected );
		if ( ! ready ) return;
		beforeSwitch?.();
		open();

	}

	function useFile( file ) {

		if ( ! file ) return;
		userFile = file;
		selected = 'file';
		render();
		switchModel();

	}

	els.modelsButton.addEventListener( 'click', () => setOpen( els.modelsPanel.hidden ) );
	els.modelChip.addEventListener( 'click', () => setOpen( els.modelsPanel.hidden ) );
	els.modelsClose.addEventListener( 'click', () => setOpen( false ) );
	els.modelsList.addEventListener( 'click', ( event ) => {

		if ( event.target.closest( '[data-action="file"]' ) ) {

			setOpen( false );
			els.file.value = '';
			els.file.click();
			return;

		}

		const item = event.target.closest( '[data-id]' );
		if ( ! item ) return;
		setOpen( false );
		pick( item.dataset.id );

	} );

	// Up and Down move between rows; Esc closes before it can stop a run.
	els.modelsPanel.addEventListener( 'keydown', ( event ) => {

		if ( event.key === 'Escape' ) {

			event.stopPropagation();
			setOpen( false );
			els.modelsButton.focus();
			return;

		}

		if ( event.key !== 'ArrowDown' && event.key !== 'ArrowUp' ) return;
		const rows = [ ...els.modelsList.querySelectorAll( 'button' ) ];
		event.preventDefault();
		const at = rows.indexOf( document.activeElement );
		const next = event.key === 'ArrowDown' ? Math.min( rows.length - 1, at + 1 ) : Math.max( 0, at - 1 );
		rows[ at === - 1 ? 0 : next ]?.focus();

	} );

	document.addEventListener( 'pointerdown', ( event ) => {

		if ( els.modelsPanel.hidden ) return;
		const inside = [ els.modelsPanel, els.modelsButton, els.modelChip ].some( ( node ) => node.contains( event.target ) );
		if ( ! inside ) setOpen( false );

	} );
	els.file.addEventListener( 'change', () => useFile( els.file.files?.[ 0 ] ) );

	// Capture phase, so a library drop handler on the viewport does not also
	// open the file. The picker has to stay in step with what is loaded.
	els.right.addEventListener( 'dragover', ( event ) => {

		event.preventDefault();
		event.stopImmediatePropagation();

	}, true );
	els.right.addEventListener( 'drop', ( event ) => {

		event.preventDefault();
		event.stopImmediatePropagation();
		const file = [ ...( event.dataTransfer?.files ?? [] ) ].find( ( item ) => acceptsName( accept, item.name ) );
		if ( file ) useFile( file );
		else out.warn( `Drop a ${ accept } file to load it` );

	}, true );

	return {
		current,
		ensure,
		open,
		render() {

			const keep = store.get( 'model' );
			selected = entries.some( ( item ) => item.id === keep && item.id !== 'file' ) ? keep : fallbackId();
			render();

		},
		/** True while the picked model is not open yet. */
		pending() {

			return ! loaded || !! loading;

		},
		/** The library is up: picks from now on open straight away. */
		enable() {

			ready = true;

		},
	};

}
